import { action, autorun, makeObservable, observable, reaction, toJS } from 'mobx';

import type { Challenge, ChallengesResponse } from '../../dto/challenges.types';
import { getRequest } from '../../services/api';
import { getUserStats, ldClient, resolveFlag } from '../../services/launchDarkly';
import { devError } from '../../utils/log';
import type { FeatureManagerDialogType, IFeatureManagerStore } from '../feature-manager/types';
import type { RootStore } from '../RootStore';
import { Loadable } from '../traits/Loadable';
import { EnumOpenDialog } from '../ui/types';

export class ChallengesStore extends Loadable implements IFeatureManagerStore {
  protected readonly rootStore: RootStore;

  @observable
  public iframeRef: HTMLIFrameElement | null = null;

  @observable
  public challengesUrl: string | undefined = undefined;

  @action
  public setChallengesUrl(url: string) {
    this.challengesUrl = url;
  }

  public setIframeRef = (iframe: HTMLIFrameElement | null) => {
    this.iframeRef = iframe;
  };

  @observable
  public challengesFlag = false;

  @observable
  public challenges: { SC?: Challenge[]; GC?: Challenge[] } = {};

  @observable
  public challengesLength: number = 0;

  @observable
  public showDialog: boolean = false;

  @observable
  public activeMissionProgressByCoinType: {
    GC?: { current: number; target: number; expiresAt?: string | null };
    SC?: { current: number; target: number; expiresAt?: string | null };
  } = {};
  constructor(rootStore: RootStore) {
    super();
    this.rootStore = rootStore;
    makeObservable(this);

    ldClient?.on('ready', () => {
      this.resolveAndUpdateFlags();
    });

    ldClient?.on('change', (changes) => {
      if ('challenges_feat' in changes) {
        this.resolveAndUpdateFlags();
      }
    });

    autorun(() => {
      this.challengesLength = (this.challenges.GC?.length || 0) + (this.challenges.SC?.length || 0);
    });

    reaction(
      () => ({
        isAuthenticationFlowDone: this.rootStore.authStore.isAuthenticationFlowDone,
        authorized: this.rootStore.authStore.authorized,
      }),
      ({ isAuthenticationFlowDone, authorized }) => {
        if (isAuthenticationFlowDone && authorized) {
          this.resolveAndUpdateFlags();
        }
      },
      { fireImmediately: true },
    );
  }

  private resolveAndUpdateFlags() {
    if (!this.rootStore.authStore.authorized || !this.rootStore.userStore.profile) {
      this.challengesFlag = false;
      return;
    }

    this.challengesFlag = resolveFlag('challenges_feat', true);
  }

  public dialogTypes: FeatureManagerDialogType[] = ['Challenges'];

  @action
  public setChallenges(gcChallenges: Challenge[], scChallenges?: Challenge[]) {
    this.challenges = { SC: scChallenges, GC: gcChallenges };
  }

  @action
  public setActiveMissionProgressForCoinType(
    coinType: 'GC' | 'SC',
    current: number,
    target: number,
    expiresAt?: string | null,
  ) {
    if (current === 0 && target === 0) {
      this.activeMissionProgressByCoinType[coinType] = undefined;
      return;
    }

    this.activeMissionProgressByCoinType[coinType] = { current, target, expiresAt };
  }

  @action
  public setShowDialog(showDialog: boolean) {
    this.showDialog = showDialog;
  }
  public async fetch() {
    if (!this.challengesFlag) return 'OFF';

    if (this.loading) return 'FETCHING';

    this.setLoading(true);
    try {
      const response = await getRequest<{ data: ChallengesResponse }>('challenges');
      if (response.data.data) {
        const data = response.data.data;

        this.setChallenges(data.GC?.challenges || data.challenges || [], data.SC?.challenges || undefined);

        // GC: prefer per-coinType state from new API, fallback to root-level for old API
        const gcState = data.GC?.activeChallengeState ?? data.activeChallengeState;
        if (gcState) {
          this.setActiveMissionProgressForCoinType('GC', gcState.current, gcState.target, gcState.expiresAt);
        } else {
          this.setActiveMissionProgressForCoinType('GC', 0, 0, null);
        }

        // SC: only available in new API
        const scState = data.SC?.activeChallengeState;
        if (scState) {
          this.setActiveMissionProgressForCoinType('SC', scState.current, scState.target, scState.expiresAt);
        } else {
          this.setActiveMissionProgressForCoinType('SC', 0, 0, null);
        }

        this.setShowDialog(data.showDialog);
        return 'READY';
      }
      return 'OFF';
    } catch (error) {
      return 'ERROR';
    } finally {
      this.setLoading(false);
    }
  }

  public handleChallengesIframeLoad(iframeRef: HTMLIFrameElement) {
    if (!iframeRef.contentWindow) {
      devError('ChallengesStore handleChallengesIframeLoad: Iframe content window is not available');
      return;
    }

    this.setIframeRef(iframeRef);

    if (this.challenges) {
      this.sendChallengesGameUiMessage({
        type: 'newChallengesEvent',
        payload: {
          challenges: JSON.parse(JSON.stringify(toJS(this.challenges))),
        },
      });
    }

    if (this.rootStore.userStore.profile) {
      this.sendChallengesGameUiMessage({
        type: 'userReady',
        payload: {
          userProfile: getUserStats(this.rootStore.userStore.profile?.stats),
          language: this.rootStore.userStore.profile.language,
        },
      });
    }
  }

  sendChallengesGameUiMessage({ type, payload }: { type: string; payload: any }) {
    if (!this.iframeRef || !this.iframeRef.contentWindow) {
      devError('ChallengesStore sendChallengesGameUiMessage: Iframe content window is not available');
      return;
    }

    this.iframeRef.contentWindow.postMessage(
      {
        type,
        payload,
      },
      import.meta.env.VITE_GAME_UI_URL,
    );
  }

  public startChallengesDialog() {
    const { coinType: currentCoinType } = this.rootStore.coinTypeStore;
    const path = 'challenges';

    const url = this.rootStore.gameUiStore.getGameUiURL(path, {
      coinType: currentCoinType,
      flags: JSON.stringify([{ name: 'enable_features_dates', value: resolveFlag('enable_features_dates', false) }]),
    });

    this.setChallengesUrl(url);

    this.rootStore.uiStore.setOpenDialog({
      dialog: EnumOpenDialog.isOpenChallengeDialog,
      options: { url },
    });
  }

  public closeGame() {
    this.setIframeRef(null);
    this.rootStore.uiStore.setCloseDialog();
    this.rootStore.featureManagerStore.onDialogFinished('Challenges');
  }

  startDialogs(type?: FeatureManagerDialogType | undefined) {
    if (type && ['Challenges'].includes(type)) {
      if (!this.showDialog) {
        return false;
      }

      void this.startChallengesDialog();
      return true;
    }
    return false;
  }
}
